import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Tooltip,
  Grid,
  IconButton,
  Card,
  CardMedia,
  Typography,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
} from "@mui/material";
import {
  apiCallDeleteMedia,
  apiCallMediaVenue,
  apiCallMediaVenueAdd,
} from "../../../../Core/Apicall";
import { useSelector } from "react-redux";
import { RootState } from "../../../../Redux/Store";
import { UploadImages } from "../../../../Core/UploadImages";
import DeleteIcon from "@mui/icons-material/Delete";
import uploadImage from "../../../../assets/Images/Upload.png";
import ic_play from "../../../../assets/Images/ic_play.png";
import { toast } from "react-toastify";
import VenueTopMediaPhotoGallery from "../../../Common/ImageGallery/VenueTopMediaPhotoGallery";
import imageCompression from "browser-image-compression";

type VenueMedia = {
  _id: string;
  venueId: string;
  thumbnailMediaUrl: string;
  mediaUrl: string;
  mediaType: string;
};

const VenueMediaList: React.FC<{ venueId: string; venueName: any }> = ({
  venueId,
  venueName,
}) => {
  const [mediaList, setMediaList] = useState<VenueMedia[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<string | null>(null);
  const [openPreview, setOpenPreview] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const userdata: any = useSelector((state: RootState) => state.global.token);

  const rowsPerPage = 12;

  const fetchMedia = async (newPage = 1) => {
    try {
      setLoading(true);
      const res = await apiCallMediaVenue({
        venueId,
        offset: newPage,
        limit: rowsPerPage,
      });

      setTotalCount(res?.totalCount);

      if (res.data) {
        if (newPage === 1) {
          setMediaList(res.data);
        } else {
          setMediaList((prev) => [...prev, ...res.data]);
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMedia(1);
  }, [venueId]);

  // 🟢 Handle file upload with inline progress card
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);

    await Promise.all(
      fileArray.map(async (file) => {
        const tempId = `temp-${Date.now()}-${Math.random()}`;

        let finalFile = file;

        // 🔹 If image → compress before upload
        if (file.type.startsWith("image/")) {
          try {
            const options = {
              maxSizeMB: 1,
              maxWidthOrHeight: 1920,
              useWebWorker: true,
            };

            const compressedFile = await imageCompression(file, options);

            finalFile = compressedFile;
          } catch (error) {
            console.log("Compression failed, using original file");
          }
        }

        const tempMedia: VenueMedia = {
          _id: tempId,
          venueId,
          thumbnailMediaUrl: "",
          mediaUrl: URL.createObjectURL(finalFile),
          mediaType: "Uploading_0",
        };

        setMediaList((prev) => [tempMedia, ...prev]);

        try {
          const uploadRes = await UploadImages(
            finalFile,
            "venue_media",
            undefined,
            undefined,
            venueId,
            (percent: number) => {
              setMediaList((prev) =>
                prev.map((item) =>
                  item._id === tempId
                    ? { ...item, mediaType: `Uploading_${percent}` }
                    : item
                )
              );
            }
          );

          if (!uploadRes?.location) {
            setMediaList((prev) => prev.filter((m) => m._id !== tempId));
            return;
          }

          await apiCallMediaVenueAdd({
            venueId,
            mediaUrl: uploadRes.location,
            thumbnailMediaUrl: uploadRes.thumbnail,
            mediaType: file.type.startsWith("video/") ? "Video" : "Image",
            userId: userdata?.data?.userId,
          });

          setMediaList((prev) => prev.filter((m) => m._id !== tempId));
        } catch {
          setMediaList((prev) => prev.filter((m) => m._id !== tempId));
        }
      })
    );

    fetchMedia(1);
    e.target.value = "";
  };

  const handleDelete = async (id: string) => {
    try {
      await apiCallDeleteMedia({
        userId: userdata?.data?.userId,
        venueMediaId: id,
        isDelete: true,
      });
      setMediaList((prev) => prev.filter((m) => m._id !== id));
      toast.success("Deleted successfully");
      fetchMedia();
    } catch (error) { }
  };

  const hasMore = page * rowsPerPage < totalCount;

  const handleLoadMoreGallery = async () => {
    if (!hasMore || loading) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchMedia(nextPage);
  };

  return (
    <div style={{ width: "100%" }}>
      <div style={{ display: "flex", gap: "20px" }}>
        {/* Upload Box */}
        <Tooltip style={{ width: "100px" }} title="Upload Image/Video">
          <Box
            position="relative"
            display="inline-flex"
            sx={{ width: 100, height: 100 }}
          >
            <IconButton
              component="label"
              sx={{
                p: 3,
                border: "2px dashed #999",
                borderRadius: 3,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                width: 100,
                height: 100,
                cursor: "pointer",
              }}
            >
              <img
                src={uploadImage}
                alt="Upload"
                style={{ width: 40, height: 40, opacity: 0.7 }}
              />
              <input
                onChange={handleFileUpload}
                hidden
                type="file"
                accept="image/*,video/*"
                multiple
              />
            </IconButton>
          </Box>
        </Tooltip>

        {/* Media List */}
        {loading ? (
          <CircularProgress />
        ) : (
          <Grid container spacing={2} wrap="wrap">
            {mediaList.map((media) => (
              <Grid key={media._id}>
                <Card
                  sx={{
                    position: "relative",
                    borderRadius: 2,
                    overflow: "hidden",
                    boxShadow: 3,
                    width: 100,
                    height: 100,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {/* 🟣 Uploading Progress */}
                  {media.mediaType.startsWith("Uploading") ? (
                    <>
                      <CircularProgress
                        variant="determinate"
                        value={parseInt(media.mediaType.split("_")[1] || "0")}
                        size={50}
                        sx={{
                          color: "#000",
                        }}
                        thickness={3.5}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          position: "absolute",
                          color: "#000",
                          fontWeight: "bold",
                        }}
                      >
                        {media.mediaType.split("_")[1] || 0}%
                      </Typography>
                    </>
                  ) : (
                    <>
                      {/* Delete Button */}
                      <IconButton
                        size="small"
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          zIndex: 5,
                          bgcolor: "rgba(255,255,255,0.8)",
                          "&:hover": {
                            bgcolor: "rgba(255,0,0,0.9)",
                            color: "white",
                          },
                        }}
                        onClick={() => setDeleteDialogOpen(media._id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>

                      {/* Delete Confirmation Dialog */}
                      {deleteDialogOpen === media._id && (
                        <Dialog
                          open={Boolean(deleteDialogOpen)}
                          onClose={() => setDeleteDialogOpen(null)}
                        >
                          <DialogTitle>
                            Are you sure you want to delete this media?
                          </DialogTitle>
                          <DialogActions>
                            <Button onClick={() => setDeleteDialogOpen(null)}>
                              Cancel
                            </Button>
                            <Button
                              color="error"
                              variant="contained"
                              onClick={() => handleDelete(media._id)}
                            >
                              Delete
                            </Button>
                          </DialogActions>
                        </Dialog>
                      )}

                      {/* Image / Video */}
                      {media.mediaType === "Image" ? (
                        <CardMedia
                          component="img"
                          image={media.mediaUrl}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            const idx = mediaList.findIndex(
                              (m) => m._id === media._id,
                            );
                            setSelectedIndex(idx);
                            setOpenPreview(true);
                          }}
                        />
                      ) : media.mediaType === "Video" ? (
                        <>
                          <CardMedia
                            component="img"
                            image={media?.thumbnailMediaUrl || media.mediaUrl}
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              cursor: "pointer",
                              opacity: 0.9,
                            }}
                            onClick={() => {
                              const idx = mediaList.findIndex(
                                (m) => m._id === media._id,
                              );
                              setSelectedIndex(idx);
                              setOpenPreview(true);
                            }}
                          />
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              pointerEvents: "none",
                            }}
                          >
                            <Box
                              sx={{
                                width: 30,
                                height: 30,
                                borderRadius: "50%",
                                backgroundColor: "rgba(0,0,0,0.6)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <img
                                src={ic_play}
                                alt="Play"
                                style={{
                                  width: 10,
                                  height: 10,
                                  objectFit: "cover",
                                }}
                              />
                            </Box>
                          </Box>
                        </>
                      ) : (
                        <Typography variant="caption">Unsupported</Typography>
                      )}
                    </>
                  )}
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </div>
      <Box display="flex" justifyContent="center" mt={2} gap={4}>
        {hasMore && !loading && (
          <Box
            onClick={() => {
              const nextPage = page + 1;
              setPage(nextPage);
              fetchMedia(nextPage);
            }}
            sx={{
              cursor: "pointer",
              px: 1.8,
              py: 0.6,
              borderRadius: "18px",
              background: "linear-gradient(135deg, #4A90E2, #007AFF)",
              color: "white",
              fontWeight: 500,
              fontSize: "12px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
              transition: "0.2s",
              display: "inline-flex",
              alignItems: "center",
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
              },
            }}
          >
            View More
          </Box>
        )}
        {page > 1 && (
          <Box
            onClick={() => {
              setPage(1);
              fetchMedia(1);
            }}
            sx={{
              cursor: "pointer",
              px: 1.8,
              py: 0.6,
              borderRadius: "18px",
              background: "linear-gradient(135deg, #FF6B6B, #FF3B30)",
              color: "white",
              fontWeight: 500,
              fontSize: "12px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
              transition: "0.2s",
              display: "inline-flex",
              alignItems: "center",
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
              },
            }}
          >
            View Less
          </Box>
        )}
      </Box>

      {openPreview && (
        <VenueTopMediaPhotoGallery
          open={openPreview}
          venueName={venueName}
          mediaList={mediaList}
          selectedIndex={selectedIndex}
          onClose={() => setOpenPreview(false)}
          onEndReached={handleLoadMoreGallery}
        />
      )}
    </div>
  );
};

export default VenueMediaList;
